import type { UserPrivileges } from '@investigator/shared/providers';

/**
 * Checks if ChatGPT Private Data privilege is enabled for a given tenant.
 *
 * @param userPrivileges - The user privileges object containing tenant-specific privileges
 * @param tenantId - The tenant ID to check privileges for
 * @returns true if the chatgpt_private_data privilege is enabled for the tenant, false otherwise
 *
 * @example
 * ```typescript
 * const { userPrivileges } = useContext(AuthContext);
 * const isEnabled = isChatGPTEnabled(userPrivileges, tenantId);
 * ```
 */
export const isChatGPTEnabled = (
  userPrivileges: Record<string, UserPrivileges> | undefined,
  tenantId: string,
): boolean => {
  if (!tenantId || !userPrivileges) {
    return false;
  }

  return userPrivileges[tenantId]?.chatgpt_private_data === true;
};

/**
 * Checks if ChatGPT Private Data privilege is enabled for a given tenant privileges object.
 * This is a more direct check when you already have the tenant-specific privileges.
 *
 * @param tenantPrivileges - The tenant-specific privileges object
 * @returns true if the chatgpt_private_data privilege is enabled, false otherwise
 *
 * @example
 * ```typescript
 * const tenantPrivileges = userPrivileges?.[tenantId];
 * const isEnabled = hasChatGPTPrivilege(tenantPrivileges);
 * ```
 */
export const hasChatGPTPrivilege = (
  tenantPrivileges: UserPrivileges | undefined,
): boolean => {
  return tenantPrivileges?.chatgpt_private_data === true;
};
